import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { SvgIconComponent, SvgIcons } from '@ngneat/svg-icon';
import { ScheduleCalendarElement, selectIsEaglesoftPms } from '@libs/store';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { AppointmentStatusCodeV2 } from '@ffd/api-client';
import { CdkMenuModule } from '@angular/cdk/menu';
import { DynamicRoute, DynamicRouterLinkDirective, TagEvent, TagPusherService } from '@libs/shared-services';
import { getStatusColorHex, getStatusTextColor, getStatusAbbreviation } from '../appointment-status-chip';

interface PatientNavItem {
  routerLink: DynamicRoute;
  icon: SvgIcons;
  label: string;
  onTagPush?: () => void;
  fromHome?: boolean;
}

@Component({
  selector: 'lib-appointment-context-menu',
  templateUrl: './appointment-context-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, SvgIconComponent, TranslateModule, CommonModule, CdkMenuModule, DynamicRouterLinkDirective],
})
export class AppointmentContextMenuComponent {
  private readonly tagPusherService = inject(TagPusherService);
  private readonly store = inject(Store);

  protected readonly isEaglesoftPms = this.store.selectSignal(selectIsEaglesoftPms);

  @Input({ required: true }) public vm!: ScheduleCalendarElement;
  @Input() public statusOptions: AppointmentStatusCodeV2[] = [];
  @Input() public areTreatmentsLoaded: boolean = false;

  @ViewChild('appointmentContextMenu', { static: true }) public appointmentContextMenu!: TemplateRef<unknown>;

  @Output() public setComplete = new EventEmitter<void>();
  @Output() public editStatus = new EventEmitter<void>();
  @Output() public editAppointment = new EventEmitter<void>();
  @Output() public deleteAppointment = new EventEmitter<void>();
  @Output() public openHealthHistory = new EventEmitter<void>();
  @Output() public changeStatus = new EventEmitter<string>();

  protected TagEvent = TagEvent;

  protected readonly patientNavItems: PatientNavItem[] = [
    {
      routerLink: 'chartingOnSiteForPatient',
      icon: 'px-24-chart',
      label: 'patientSearch_chart_button',
      onTagPush: () =>
        this.tagPusherService.pushTag({ event: TagEvent.ChartingOpened, location: 'schedule', patient_id: this.vm.patientId! }),
      fromHome: true,
    },
    { routerLink: 'imagingOnSiteForPatient', icon: 'px-32-digital', label: 'patientSearch_digital_button' },
    { routerLink: 'perioOnSiteForPatient', icon: 'px-32-perio', label: 'patientSearch_perio_button' },
    { routerLink: 'notesOnSiteForPatient', icon: 'px-24-edit-note', label: 'patientSearch_notes_button' },
    { routerLink: 'collabOnSiteForPatient', icon: 'px-24-collab', label: 'patientSearch_collab_button' },
  ];

  protected onPatientNavItemClick(item: PatientNavItem): void {
    item.onTagPush?.();
  }

  public onSetComplete(): void {
    this.setComplete.emit();
  }

  public onEditStatus(): void {
    this.editStatus.emit();
  }

  public onEditAppointment(): void {
    this.editAppointment.emit();
  }

  public onDeleteAppointment(): void {
    this.deleteAppointment.emit();
  }

  public onOpenHealthHistory(): void {
    this.openHealthHistory.emit();
  }

  public onChangeStatus(statusId: string): void {
    this.changeStatus.emit(statusId);
  }

  public getStatusBackgroundColor(status: AppointmentStatusCodeV2): string {
    return getStatusColorHex(status.statusColor) || '#ffffff';
  }

  public getStatusTextColor(status: AppointmentStatusCodeV2): string {
    return getStatusTextColor(status.statusColor);
  }

  public getStatusCode(status: AppointmentStatusCodeV2): string {
    return getStatusAbbreviation(status.statusColor);
  }
}
